import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { createReferenceEqualSelector } from '../../selectors/utils';
import { setMarket } from '../intl';
import { CATEGORIZATION, NODE_COPY, NODE_CUT } from '../../../utils/command';
import type { RootState } from '../..';
import type { NodeBaseItem } from '../../../entities/Node';

interface TreeCommand {
  directive: string;
  tree: string;
  nodeItems: NodeBaseItem[];
}

interface TreeDragState {
  action: TreeCommand | null;
}

const initialState: TreeDragState = {
  action: null,
};

// Standalone action creators for cut/copy/categorize
export const categorize = (treeId: string, items: NodeBaseItem[]) => ({
  type: 'treeDrag/command',
  meta: { treeId },
  payload: {
    directive: CATEGORIZATION,
    nodeItems: items,
  },
});

export const copyNode = (treeId: string, items: NodeBaseItem[]) => ({
  type: 'treeDrag/command',
  meta: { treeId },
  payload: {
    directive: NODE_COPY,
    nodeItems: items,
  },
});

export const cutNode = (treeId: string, items: NodeBaseItem[]) => ({
  type: 'treeDrag/command',
  meta: { treeId },
  payload: {
    directive: NODE_CUT,
    nodeItems: items,
  },
});

const treeDragSlice = createSlice({
  name: 'treeDrag',
  initialState,
  reducers: {
    resetDirective(state) {
      state.action = null;
    },
    setCommand(
      state,
      action: PayloadAction<{
        treeId: string;
        directive: string;
        nodeItems: NodeBaseItem[];
      }>,
    ) {
      const { treeId, directive, nodeItems } = action.payload;
      state.action = {
        directive,
        tree: treeId,
        nodeItems,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setMarket, () => ({ ...initialState }))
      .addMatcher(
        (
          action,
        ): action is {
          type: string;
          meta: { treeId: string };
          payload: { directive: string; nodeItems: NodeBaseItem[] };
        } => action.type === 'treeDrag/command',
        (state, action) => {
          const {
            meta: { treeId },
            payload: { directive, nodeItems },
          } = action;
          state.action = {
            directive,
            tree: treeId,
            nodeItems,
          };
        },
      );
  },
});

export const { resetDirective, setCommand } = treeDragSlice.actions;

export default treeDragSlice.reducer;

// Selectors
const selectTreeDrag = (state: RootState) => state.treeDrag;

export const selectCurrentCommand = createReferenceEqualSelector(
  selectTreeDrag,
  (state) => state.action ?? { directive: '', tree: '', nodeItems: [] },
);

export const selectHasActiveCommand = createReferenceEqualSelector(
  selectTreeDrag,
  (state) => state.action !== null,
);

export const selectCommandDirective = createReferenceEqualSelector(
  selectTreeDrag,
  (state) => state.action?.directive ?? null,
);

export const selectCommandNodeItems = createReferenceEqualSelector(
  selectTreeDrag,
  (state) => state.action?.nodeItems ?? [],
);

export const selectCommandTreeId = createReferenceEqualSelector(
  selectTreeDrag,
  (state) => state.action?.tree ?? null,
);
